import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Bell,
  Search,
  User,
  LogOut,
  Settings,
  ChevronDown,
  AlertTriangle,
  Info,
  CheckCircle,
  Sparkles,
  Package,
  ShoppingCart,
  Users
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useAuditorStore } from '../../stores/auditorStore'
import { useNavigationStore } from '../../stores/navigationStore'
import { apiClient } from '../../services/api'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { ThemeToggle } from '../ui/ThemeToggle'
import { ConnectionStatus } from '../ui/ConnectionStatus'
import { BusinessSwitcher } from '../auditor/BusinessSwitcher'
import { cn } from '../../lib/utils'
import { formatCurrency } from '../../lib/utils'

interface HeaderProps {
  className?: string
}

interface Notification {
  id: number
  type: 'warning' | 'info' | 'success'
  message: string
  time: string
}

export const Header = ({ className }: HeaderProps) => {
  const { user, logout } = useAuthStore()
  const { isAuditor } = useAuditorStore()
  const { navigationStyle } = useNavigationStore()
  const navigate = useNavigate()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any>(null)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const searchRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const dashboardResponse = await apiClient.getDashboardStats().catch(() => ({
          low_stock_products: []
        }))

        // Generate real notifications based on data
        const realNotifications: Notification[] = []

        if (dashboardResponse.low_stock_products?.length > 0) {
          realNotifications.push({
            id: Date.now(),
            type: 'warning',
            message: `${dashboardResponse.low_stock_products.length} product(s) are low on stock`,
            time: 'Just now'
          })
        }

        setNotifications(realNotifications)
      } catch (error) {
        console.error('Failed to load notifications:', error)
      }
    }

    if (user) {
      loadNotifications()
    }
  }, [user])

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search
  const handleSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults(null)
      setShowSearchResults(false)
      return
    }

    setSearchLoading(true)
    try {
      const results = await apiClient.globalSearch(query, 5)
      setSearchResults(results)
      setShowSearchResults(true)
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults(null)
    } finally {
      setSearchLoading(false)
    }
  }, [])

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query)
    }, 300)
  }

  const handleResultClick = (type: 'product' | 'order' | 'customer', id: string) => {
    setShowSearchResults(false)
    setSearchQuery('')
    setSearchResults(null)

    // Navigate to the appropriate page
    if (type === 'product') {
      // Navigate to product edit page
      navigate(`/products/edit/${id}`)
    } else if (type === 'order') {
      // Check if it's an invoice or regular sale
      const order = searchResults?.orders?.find((o: any) => o.id === id)
      if (order?.transaction_type === 'invoice' && order?.invoice_number) {
        // Navigate to invoice detail
        navigate(`/invoices/${id}`)
      } else {
        // Navigate to orders page (sales list)
        navigate('/orders')
      }
    } else if (type === 'customer') {
      // Navigate to customers page
      navigate('/customers')
    }
  }

  const unreadCount = notifications.length

  return (
    <header className={cn("bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-colors", className)}>
      <div className="flex items-center justify-between h-16 px-6">
        {/* Brand Section - Only for horizontal navigation */}
        {navigationStyle === 'horizontal' && (
          <div className="flex items-center space-x-3 pr-6 mr-4 border-r border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Sparkles className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {user?.business_name || 'digiBills'}
              </h1>
            </div>
          </div>
        )}

        {/* Search (for merchants) or Business Switcher (for auditors) */}
        <div className="flex items-center flex-1 max-w-lg">
          {isAuditor ? (
            <BusinessSwitcher />
          ) : (
            <div className="relative w-full" ref={searchRef}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products, orders, customers..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={() => searchResults && setShowSearchResults(true)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
              />

              {/* Search Results Dropdown */}
              {showSearchResults && searchResults && (
                <div className="absolute top-full mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
                  {searchLoading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      Searching...
                    </div>
                  ) : (
                    <>
                      {/* Products */}
                      {searchResults.products?.length > 0 && (
                        <div className="p-2">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center">
                            <Package className="w-3 h-3 mr-1" />
                            Products
                          </div>
                          {searchResults.products.map((product: any) => (
                            <button
                              key={product.id}
                              onClick={() => handleResultClick('product', product.id)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {product.sku && `SKU: ${product.sku} • `}
                                {formatCurrency(product.price)}
                                {product.category_name && ` • ${product.category_name}`}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Orders */}
                      {searchResults.orders?.length > 0 && (
                        <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center">
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            Orders
                          </div>
                          {searchResults.orders.map((order: any) => (
                            <button
                              key={order.id}
                              onClick={() => handleResultClick('order', order.id)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {order.invoice_number || order.bill_number || 'Order'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {formatCurrency(order.total_amount)}
                                {order.customer_name && ` • ${order.customer_name}`}
                                {order.payment_status && ` • ${order.payment_status}`}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Customers */}
                      {searchResults.customers?.length > 0 && (
                        <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            Customers
                          </div>
                          {searchResults.customers.map((customer: any) => (
                            <button
                              key={customer.id}
                              onClick={() => handleResultClick('customer', customer.id)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            >
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{customer.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {customer.phone}
                                {customer.email && ` • ${customer.email}`}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* No results */}
                      {!searchResults.products?.length && !searchResults.orders?.length && !searchResults.customers?.length && (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          No results found
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Theme toggle */}
          <ThemeToggle className="hidden sm:flex" />

          {/* Connection Status */}
          <ConnectionStatus className="hidden lg:flex" />

          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <Badge
                  variant="destructive"
                  size="sm"
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </Button>

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                    <Badge variant="secondary" size="sm">{unreadCount} new</Badge>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <div className="flex items-start space-x-3">
                        <div className={cn(
                          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                          notification.type === 'warning' && "bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400",
                          notification.type === 'info' && "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400",
                          notification.type === 'success' && "bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400"
                        )}>
                          {notification.type === 'warning' && <AlertTriangle className="w-4 h-4" />}
                          {notification.type === 'info' && <Info className="w-4 h-4" />}
                          {notification.type === 'success' && <CheckCircle className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 dark:text-gray-100">{notification.message}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{notification.time}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="ghost" size="sm" className="w-full">
                    View all notifications
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-orange-500 rounded-full">
                <span className="text-sm font-semibold text-white">
                  {user?.full_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.full_name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>

            {/* User dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.full_name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{user?.business_name}</div>
                </div>
                <div className="py-1">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </Link>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <button
                    onClick={logout}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}